import React, { useState, useEffect } from 'react';
import { useUser, User } from '../../contexts/UserContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faSignOutAlt, faBoxOpen, faCheckCircle, faPlus,
  faTrash, faEdit, faPaperPlane, faCalendarAlt, faList, 
  faLocationCrosshairs, faInfoCircle, faMapMarkerAlt, faExchangeAlt,
  faTimes, faExclamationTriangle, faPencilAlt
} from '@fortawesome/free-solid-svg-icons';

// ---- THEME CONSTANTS ----
const THEME_TEAL = 'text-teal-600';
const THEME_BG_TEAL = 'bg-teal-600';
const THEME_BORDER_TEAL = 'border-teal-600';

type ActiveSection = 'profile' | 'address' | 'orders' | 'rfqs' | 'preferences' | 'history' | 'enquiries' | 'wishlist' | 'chats' | 'dashboard';
type PageMode = 'view' | 'edit';

// Updated Address Interface to match Backend Entity (postalCode vs pinCode)
interface Address {
    id?: number;
    postalCode?: string; // Backend sends this
    pinCode: string;     // Form uses this
    city: string;
    state: string;
    country: string;
    houseNo: string;
    areaStreet: string;
    locality: string;
    landmark: string;
    default?: boolean;
    isDefault?: boolean;
    type?: string; 
}

interface RFQ {
    id: number;
    productName: string;
    quantity: string;
    date: string;
    status: 'Open' | 'Closed' | 'Responded';
    responses: number;
}

interface ExtendedUser extends User {
    companyName?: string;
    designation?: string;
    industry?: string;
    gstNumber?: string;
    businessEmail?: string;
    website?: string;
}

// --- CONFIRMATION MODAL INTERFACE ---
interface ConfirmModalState {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
}

const BuyerProfile: React.FC = () => {
  const { user, updateUser, logout } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const urlSection = searchParams.get('section') as ActiveSection | null;
  const [activeSection, setActiveSection] = useState<ActiveSection>(urlSection || 'profile');
  const [pageMode, setPageMode] = useState<PageMode>('view');
  
  // ---- DATA STATES ----
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null); // Track which address is being edited
  
  const [newAddress, setNewAddress] = useState<Address>({ 
      pinCode: '', city: '', state: '', country: 'India', houseNo: '', areaStreet: '', locality: '', landmark: '' 
  });
  
  const [editFormData, setEditFormData] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ---- CONFIRMATION MODAL STATE ----
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
      isOpen: false, title: '', message: '', onConfirm: () => {}
  });

  // RFQ States
  const [showRFQForm, setShowRFQForm] = useState(false);
  const [rfqs, setRfqs] = useState<RFQ[]>([
      { id: 1, productName: "Industrial Ceiling Fans", quantity: "50 Pieces", date: "Oct 24, 2025", status: "Responded", responses: 3 },
      { id: 2, productName: "Copper Wire 5mm", quantity: "200 Kg", date: "Oct 20, 2025", status: "Open", responses: 0 },
  ]);
  const [newRFQ, setNewRFQ] = useState({ productName: '', quantity: '', unit: 'Pieces', targetPrice: '', description: '' });

  const getToken = () => localStorage.getItem("token");

  // ---- EFFECTS ----
  useEffect(() => {
    if (urlSection && urlSection !== activeSection) setActiveSection(urlSection);
  }, [urlSection]);

  useEffect(() => {
    if (pageMode === 'edit' && user) {
        setEditFormData((prev) => ({
            ...prev,
            ...user,
            alternateMobile: user.alternateMobile || '',
            companyName: prev?.companyName || '',
            designation: prev?.designation || '',
            industry: prev?.industry || '',
            gstNumber: prev?.gstNumber || '',
            businessEmail: prev?.businessEmail || '',
            website: prev?.website || ''
        }));
        fetchCompanyDetails();
    }
    if (activeSection === 'address' && user?.id) fetchAddresses();
  }, [pageMode, user, activeSection]);

  const fetchCompanyDetails = async () => {
      const token = getToken();
      try {
          const res = await fetch(`http://localhost:2027/api/buyers/${user?.id}/company`, {
             headers: { 'Authorization': `Bearer ${token}` }
          });
          if(res.ok) {
              const companyData = await res.json();
              setEditFormData(prev => ({
                  ...prev!,
                  companyName: companyData.companyName || '',
                  designation: companyData.designation || '',
                  industry: companyData.industry || '',
                  gstNumber: companyData.gstNumber || '',
                  businessEmail: companyData.businessEmail || '',
                  website: companyData.website || '',
              }));
          }
      } catch(e) { console.error(e); }
  };

  const fetchAddresses = async () => {
    const token = getToken();
    try {
        const res = await fetch(`http://localhost:2027/api/addresses/${user?.id}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if(res.ok) setAddresses(await res.json());
    } catch(e) { console.error(e); }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
        setMessage({ type: 'error', text: 'Geolocation is not supported by your browser' });
        return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
            const data = await response.json();
            if (data && data.address) {
                const addr = data.address;
                setNewAddress(prev => ({
                    ...prev,
                    pinCode: addr.postcode || '',
                    city: addr.city || addr.town || addr.village || addr.county || '',
                    state: addr.state || '',
                    country: addr.country || 'India',
                    houseNo: addr.house_number || addr.building || '',
                    areaStreet: addr.road || addr.pedestrian || '',
                    locality: addr.suburb || addr.neighbourhood || addr.residential || '',
                    landmark: prev.landmark || ''
                }));
                setMessage({ type: 'success', text: 'Location details fetched successfully!' });
            }
        } catch (error) { setMessage({ type: 'error', text: 'Could not fetch address details.' }); } 
        finally { setLocationLoading(false); }
    }, (error) => {
        setLocationLoading(false);
        setMessage({ type: 'error', text: 'Unable to retrieve location. Check permissions.' });
    });
  };

  const handlePincodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(/\D/g, ''); 
      setNewAddress(prev => ({ ...prev, pinCode: val }));
      if (val.length === 6) {
          try {
              const res = await fetch(`https://api.postalpincode.in/pincode/${val}`);
              const data = await res.json();
              if(data[0].Status === "Success") {
                  const details = data[0].PostOffice[0];
                  setNewAddress(prev => ({ ...prev, pinCode: val, city: details.District, state: details.State }));
              } 
          } catch(e) { console.error("Pincode API Failed", e); }
      }
  };

  // --- ADDRESS FORM SUBMISSION (ADD or UPDATE) ---
  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!newAddress.pinCode || !newAddress.houseNo || !newAddress.areaStreet) {
        setMessage({type:'error', text:'Please fill required fields (PIN, House No, Street).'}); return;
    }
    setLoading(true);
    const token = getToken();
    
    try {
        let url = `http://localhost:2027/api/addresses/${user?.id}`; // Default POST URL
        let method = 'POST';

        // If Editing, switch to PUT and use specific address ID
        if (editingAddressId) {
            url = `http://localhost:2027/api/addresses/${editingAddressId}`;
            method = 'PUT';
        }

        const res = await fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(newAddress)
        });

        if(res.ok) {
            fetchAddresses(); 
            resetAddressForm();
            setMessage({type: 'success', text: editingAddressId ? 'Address updated successfully!' : 'Address added successfully!'});
        } else {
            setMessage({type: 'error', text: 'Failed to save address.'});
        }
    } catch(e) { setMessage({type:'error', text:'Server error'}); }
    finally { setLoading(false); }
  };

  // Populate form for Editing
  const triggerEditAddress = (addr: Address) => {
      setNewAddress({
          pinCode: addr.postalCode || addr.pinCode, // Handle both key names
          city: addr.city,
          state: addr.state,
          country: addr.country,
          houseNo: addr.houseNo,
          areaStreet: addr.areaStreet,
          locality: addr.locality || '',
          landmark: addr.landmark || ''
      });
      setEditingAddressId(addr.id || null);
      setShowAddressForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll up to form
  };

  const resetAddressForm = () => {
      setShowAddressForm(false);
      setEditingAddressId(null);
      setNewAddress({ pinCode: '', city: '', state: '', country: 'India', houseNo: '', areaStreet: '', locality: '', landmark: '' });
  };

  // --- MODAL HANDLERS ---
  const triggerDeleteAddress = (addrId: number) => {
      setConfirmModal({
          isOpen: true,
          title: 'Delete Address?',
          message: 'Are you sure you want to delete this address? This action cannot be undone.',
          onConfirm: () => executeDeleteAddress(addrId)
      });
  };

  const executeDeleteAddress = async (addrId: number) => {
    const token = getToken();
    try {
        await fetch(`http://localhost:2027/api/addresses/${addrId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        fetchAddresses();
        setConfirmModal(prev => ({ ...prev, isOpen: false })); 
        setMessage({type:'success', text: 'Address deleted successfully.'});
    } catch(e) { console.error(e); }
  };

  // --- PROFILE LOGIC ---
  const triggerPromoteMobile = () => {
      setConfirmModal({
          isOpen: true,
          title: 'Change Primary Mobile?',
          message: 'This will swap your Primary and Alternate mobile numbers. Are you sure?',
          onConfirm: executePromoteMobile
      });
  };

  const executePromoteMobile = async () => {
    const token = getToken();
    try {
        const res = await fetch(`http://localhost:2027/api/buyers/${user?.id}/promote-mobile`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setConfirmModal(prev => ({ ...prev, isOpen: false })); 
        
        if(res.ok) {
            setMessage({type: 'success', text: 'Mobile number updated successfully!'});
            if (user && user.alternateMobile) {
                updateUser({ ...user, mobile: user.alternateMobile, alternateMobile: user.mobile });
            }
        } else {
            const err = await res.text();
            setMessage({type: 'error', text: err || 'Failed to update.'});
        }
    } catch(e) { setMessage({type:'error', text:'Network error'}); }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFormData) return;
    setLoading(true);
    const token = getToken();
    
    try {
      const resPersonal = await fetch(`http://localhost:2027/api/buyers/${user?.id}/personal`, { 
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(editFormData)
      });
      const resCompany = await fetch(`http://localhost:2027/api/buyers/${user?.id}/company`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(editFormData)
      });

      if(resPersonal.ok && resCompany.ok) {
          const updatedPersonal = await resPersonal.json();
          const updatedCompany = await resCompany.json();
          updateUser({ ...updatedPersonal, ...updatedCompany }); 
          setMessage({ type: 'success', text: 'Profile updated successfully!' });
          setTimeout(() => { setPageMode('view'); setMessage(null); }, 1000);
      } else {
          let errorText = 'Update failed.';
          if(!resCompany.ok) errorText = await resCompany.text();
          else if(!resPersonal.ok) errorText = await resPersonal.text();
          setMessage({ type: 'error', text: errorText });
      }
    } catch(e) { setMessage({ type: 'error', text: 'Network error occurred.' }); }
    finally { setLoading(false); }
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault(); logout(); navigate('/'); 
  };

  const handleRFQSubmit = (e: React.FormEvent) => { e.preventDefault(); setShowRFQForm(false); setMessage({ type: 'success', text: 'Requirement posted!' }); };

  // ---- HELPER COMPONENTS ----
  
  const InfoRow = ({ label, value, isLast = false, action }: { label: string, value: React.ReactNode, isLast?: boolean, action?: React.ReactNode }) => (
      <div className={`grid grid-cols-1 md:grid-cols-3 ${!isLast ? 'border-b border-gray-100' : ''}`}>
          <div className="p-4 bg-gray-50 text-gray-500 text-sm font-semibold uppercase flex items-center">
              {label}
          </div>
          <div className="p-4 bg-white text-gray-800 font-medium md:col-span-2 flex justify-between items-center">
              <span className="break-all">{value || '- -'}</span>
              {action && <div>{action}</div>}
          </div>
      </div>
  );

  const Sidebar = () => (
    <div className="w-full md:w-[280px] flex flex-col gap-4">
      <div className="bg-white rounded-md shadow-sm p-4 flex items-center gap-4 border-b border-gray-200">
         <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200 shrink-0">
            <FontAwesomeIcon icon={faUser} className={`${THEME_TEAL} text-xl`} />
         </div>
         <div className="flex flex-col">
            <span className="text-xs text-gray-500 font-medium block">Hello,</span>
            <span className="font-bold text-gray-800 text-lg leading-tight truncate max-w-[150px] block">{user?.username || 'Guest'}</span>
         </div>
      </div>
      <div className="bg-white rounded-md shadow-sm overflow-hidden">
        <div className="border-b border-gray-100">
          <div className="px-4 py-3 bg-white flex items-center gap-3 text-gray-500 uppercase text-xs font-bold tracking-wider">
             <FontAwesomeIcon icon={faUser} className={THEME_TEAL} /> Account Settings
          </div>
          <div className="flex flex-col">
             <SidebarLink section="profile" label="Profile Information" />
             <SidebarLink section="address" label="Manage Addresses" />
             <SidebarLink section="chats" label="Chat History" />
          </div>
        </div>
        <div className="border-b border-gray-100">
          <div className="px-4 py-3 bg-white flex items-center gap-3 text-gray-500 uppercase text-xs font-bold tracking-wider">
             <FontAwesomeIcon icon={faBoxOpen} className={THEME_TEAL} /> My Stuff
          </div>
          <div className="flex flex-col">
             <SidebarLink section="orders" label="My Orders" />
             <SidebarLink section="rfqs" label="My RFQs (Quotes)" />
          </div>
        </div>
        <div className="p-4 border-t border-gray-100">
           <button onClick={handleLogout} className="flex items-center gap-3 text-sm font-semibold text-gray-600 hover:text-red-600 transition-colors w-full">
              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
           </button>
        </div>
      </div>
    </div>
  );

  const SidebarLink = ({ section, label }: { section: ActiveSection, label: string }) => {
    const isActive = activeSection === section;
    return (
      <button 
        onClick={() => { if (section === 'chats') navigate('/buyer/chats'); else setActiveSection(section); }}
        className={`px-4 py-3 text-sm text-left hover:bg-teal-50 hover:text-teal-700 transition-colors w-full ${isActive ? `bg-teal-50 ${THEME_TEAL} font-bold border-l-4 ${THEME_BORDER_TEAL}` : 'text-gray-600 border-l-4 border-transparent'}`}
      >
        {label}
      </button>
    );
  };

  // ---- MAIN RENDERER ----
  const renderContent = () => {
    const extUser = user as ExtendedUser | undefined;

    // EDIT PROFILE FORM
    if (pageMode === 'edit' && activeSection === 'profile') {
        const editInputStyle = "w-full border p-2.5 rounded focus:border-teal-600 outline-none transition-colors focus:ring-1 focus:ring-teal-100";
        const editLabelStyle = "block text-gray-600 text-xs font-bold uppercase mb-2";

        return (
            <div className="bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-xl font-bold mb-6 text-gray-800 border-b pb-4">Edit Profile</h2>
                <form onSubmit={handleEditSubmit}>
                    {/* Basic Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className={editLabelStyle}>Username</label>
                            <input type="text" value={editFormData?.username || ''} onChange={(e) => setEditFormData({...editFormData!, username: e.target.value})} className={editInputStyle} />
                        </div>
                        <div>
                            <label className={editLabelStyle}>Email (Read Only)</label>
                            <input type="text" value={editFormData?.email || ''} disabled className="w-full border p-2.5 rounded bg-gray-100 text-gray-500 cursor-not-allowed" />
                        </div>
                        <div>
                            <label className={editLabelStyle}>Mobile (Primary)</label>
                            <input type="text" value={editFormData?.mobile || ''} disabled className="w-full border p-2.5 rounded bg-gray-100 text-gray-500 cursor-not-allowed" />
                            <p className="text-[10px] text-orange-600 mt-1 flex items-center gap-1"><FontAwesomeIcon icon={faInfoCircle}/> To change, set Alternate Mobile as Primary.</p>
                        </div>
                        <div>
                            <label className={editLabelStyle}>Alternate Mobile</label>
                            <input type="text" value={editFormData?.alternateMobile || ''} onChange={(e) => setEditFormData({...editFormData!, alternateMobile: e.target.value})} className={editInputStyle} />
                        </div>
                    </div>

                    <div className="border-t border-gray-100 my-6"></div>
                    <h3 className="text-lg font-bold text-gray-700 mb-4">Company Details</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div><label className={editLabelStyle}>Company Name</label><input type="text" value={editFormData?.companyName || ''} onChange={(e) => setEditFormData({...editFormData!, companyName: e.target.value})} className={editInputStyle} /></div>
                        <div><label className={editLabelStyle}>Designation</label><input type="text" value={editFormData?.designation || ''} onChange={(e) => setEditFormData({...editFormData!, designation: e.target.value})} className={editInputStyle} /></div>
                        <div><label className={editLabelStyle}>Business Email</label><input type="email" value={editFormData?.businessEmail || ''} onChange={(e) => setEditFormData({...editFormData!, businessEmail: e.target.value})} className={editInputStyle} /></div>
                        <div><label className={editLabelStyle}>GST Number</label><input type="text" value={editFormData?.gstNumber || ''} onChange={(e) => setEditFormData({...editFormData!, gstNumber: e.target.value})} className={editInputStyle} /></div>
                        <div><label className={editLabelStyle}>Website</label><input type="text" value={editFormData?.website || ''} onChange={(e) => setEditFormData({...editFormData!, website: e.target.value})} className={editInputStyle} /></div>
                        <div><label className={editLabelStyle}>Industry</label><input type="text" value={editFormData?.industry || ''} onChange={(e) => setEditFormData({...editFormData!, industry: e.target.value})} className={editInputStyle} /></div>
                    </div>

                    <div className="flex gap-4">
                        <button type="submit" className={`${THEME_BG_TEAL} text-white px-8 py-2.5 rounded font-bold hover:bg-teal-700 shadow-sm uppercase text-sm`}>Save Changes</button>
                        <button type="button" onClick={() => setPageMode('view')} className="border border-gray-300 px-6 py-2.5 rounded font-bold text-gray-600 hover:bg-gray-50 uppercase text-sm">Cancel</button>
                    </div>
                </form>
            </div>
        )
    }

    switch (activeSection) {
      // 1. PROFILE VIEW
      case 'profile':
        return (
          <div className="bg-white rounded-md shadow-sm p-6 min-h-[400px]">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
               <h2 className="text-xl font-bold text-gray-800">My Profile</h2>
               <button onClick={() => setPageMode('edit')} className={`${THEME_TEAL} font-bold text-sm hover:underline flex items-center gap-2 px-3 py-1 rounded hover:bg-teal-50`}>
                   <FontAwesomeIcon icon={faEdit} /> Edit Profile
               </button>
            </div>

            <div className="mb-8">
                <h3 className={`${THEME_TEAL} text-sm font-bold uppercase tracking-wider mb-3`}>Personal Information</h3>
                <div className="border border-gray-200 rounded-md overflow-hidden shadow-sm">
                    <InfoRow label="Username" value={extUser?.username} />
                    <InfoRow label="Email Address" value={extUser?.email} />
                    <InfoRow 
                        label="Mobile Number" 
                        value={
                            <span className="flex items-center gap-2">
                                {extUser?.mobile} 
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" title="Verified" />
                            </span>
                        } 
                    />
                    <InfoRow 
                        label="Alternate Mobile" 
                        isLast
                        value={extUser?.alternateMobile}
                        action={extUser?.alternateMobile ? (
                            <button onClick={triggerPromoteMobile} className="text-[10px] bg-teal-50 border border-teal-200 text-teal-700 px-2 py-1 rounded hover:bg-teal-100 flex items-center gap-1 font-bold">
                                <FontAwesomeIcon icon={faExchangeAlt} /> Make Primary
                            </button>
                        ) : null}
                    />
                </div>
            </div>

            <div className="my-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-md flex items-start gap-3">
                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                <p className="text-sm text-blue-900">
                    <span className="font-bold">Business Note:</span> If you wish to purchase products in bulk for your business, please ensure your <span className="font-bold">Company Details</span> below are accurate.
                </p>
            </div>

            <div>
                <h3 className={`${THEME_TEAL} text-sm font-bold uppercase tracking-wider mb-3`}>Company Details</h3>
                <div className="border border-gray-200 rounded-md overflow-hidden shadow-sm">
                    <InfoRow label="Company Name" value={extUser?.companyName} />
                    <InfoRow label="Designation" value={extUser?.designation} />
                    <InfoRow label="Business Email" value={extUser?.businessEmail} />
                    <InfoRow label="Website" value={extUser?.website} />
                    <InfoRow label="GST Number" value={extUser?.gstNumber} />
                    <InfoRow label="Industry" value={extUser?.industry} isLast />
                </div>
            </div>
            
            <div className="mt-8 pt-4 border-t border-gray-100 text-xs text-gray-400">
                Last updated on {new Date().toLocaleDateString()}
            </div>
          </div>
        );

      // 2. ADDRESS VIEW - WITH EDIT & DELETE
      case 'address':
        const inputStyle = "w-full border border-gray-300 p-3 rounded text-sm focus:border-teal-600 outline-none transition-colors";
        const labelStyle = "block text-gray-600 text-sm font-semibold mb-2";

        return (
          <div className="bg-white rounded-md shadow-sm p-6 min-h-[500px]">
             <h2 className="text-xl font-bold text-gray-800 mb-6 border-b pb-4">Manage Addresses</h2>
             
             {!showAddressForm && (
                 <div onClick={() => { resetAddressForm(); setShowAddressForm(true); }} className="border-2 border-dashed border-gray-300 p-6 rounded-lg mb-6 cursor-pointer hover:bg-teal-50 hover:border-teal-400 flex flex-col items-center gap-2 transition-all group text-center">
                     <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-teal-200 flex items-center justify-center">
                        <FontAwesomeIcon icon={faPlus} className={`${THEME_TEAL}`} />
                     </div>
                     <span className={`${THEME_TEAL} font-bold uppercase text-sm`}>Add a new delivery address</span>
                 </div>
             )}

             {showAddressForm && (
                 <form onSubmit={handleAddressSubmit} className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6 shadow-inner relative">
                     <button type="button" onClick={resetAddressForm} className="absolute top-4 right-4 text-gray-400 hover:text-red-500"><FontAwesomeIcon icon={faTimes} size="lg" /></button>
                     
                     <div className="flex justify-between items-center mb-6">
                        <h3 className="text-base font-bold text-teal-700 uppercase">{editingAddressId ? 'Edit Address' : 'Add New Address'}</h3>
                        <button type="button" onClick={handleUseCurrentLocation} className="bg-blue-600 text-white text-xs px-4 py-2.5 rounded-md flex items-center gap-2 hover:bg-blue-700 shadow-sm font-semibold" disabled={locationLoading}>
                            {locationLoading ? <span>Detecting...</span> : <><FontAwesomeIcon icon={faLocationCrosshairs} /> Use Current Location</>}
                        </button>
                     </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                         <div><label className={labelStyle}>PIN Code</label><input type="text" maxLength={6} value={newAddress.pinCode} onChange={handlePincodeChange} className={inputStyle} required /></div>
                         <div><label className={labelStyle}>City</label><input type="text" value={newAddress.city} onChange={e=>setNewAddress({...newAddress,city:e.target.value})} className={inputStyle} required /></div>
                         <div><label className={labelStyle}>State</label><input type="text" value={newAddress.state} readOnly className={`${inputStyle} bg-gray-100 cursor-not-allowed`} tabIndex={-1} /></div>
                         <div><label className={labelStyle}>Country</label><input type="text" value={newAddress.country} readOnly className={`${inputStyle} bg-gray-100 cursor-not-allowed`} tabIndex={-1} /></div>
                         <div className="md:col-span-2"><label className={labelStyle}>House No / Building</label><input type="text" value={newAddress.houseNo} onChange={e=>setNewAddress({...newAddress,houseNo:e.target.value})} className={inputStyle} required /></div>
                         <div className="md:col-span-2"><label className={labelStyle}>Area / Street / Sector</label><input type="text" value={newAddress.areaStreet} onChange={e=>setNewAddress({...newAddress,areaStreet:e.target.value})} className={inputStyle} required /></div>
                         <div><label className={labelStyle}>Locality (Optional)</label><input type="text" value={newAddress.locality} onChange={e=>setNewAddress({...newAddress,locality:e.target.value})} className={inputStyle} /></div>
                         <div><label className={labelStyle}>Landmark (Optional)</label><input type="text" value={newAddress.landmark} onChange={e=>setNewAddress({...newAddress,landmark:e.target.value})} className={inputStyle} /></div>
                     </div>
                     <div className="flex gap-4 border-t border-gray-200 pt-6">
                        <button type="submit" className={`${THEME_BG_TEAL} text-white px-8 py-2.5 rounded text-sm font-bold uppercase shadow-sm hover:opacity-90`}>{editingAddressId ? 'UPDATE ADDRESS' : 'SAVE ADDRESS'}</button>
                        <button type="button" onClick={resetAddressForm} className="text-gray-600 border border-gray-300 px-6 py-2.5 rounded text-sm font-bold uppercase hover:bg-gray-100">Cancel</button>
                     </div>
                 </form>
             )}

             <div className="grid grid-cols-1 gap-4">
                 {addresses.map((addr) => (
                     <div key={addr.id} className="border border-gray-200 rounded-lg p-5 flex justify-between group hover:shadow-md transition-shadow bg-white relative overflow-hidden">
                         <div className={`absolute left-0 top-0 bottom-0 w-1 ${THEME_BG_TEAL}`}></div>
                         <div className="flex flex-col gap-2 pl-2">
                             <div className="flex items-center gap-3">
                                 <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded uppercase border border-gray-200">{addr.type || 'Home'}</span>
                                 <span className="font-bold text-gray-800 text-sm">{user?.username} &nbsp; {user?.mobile}</span>
                             </div>
                             <p className="text-sm text-gray-600 leading-relaxed">
                                <span className="font-semibold block">{addr.houseNo}, {addr.areaStreet}</span>
                                {addr.locality && <span>{addr.locality}, </span>}
                                {addr.city}, {addr.state} <br/>
                                {/* Display postalCode from backend, fallback to pinCode if just added locally */}
                                <span className="font-bold text-gray-800">{addr.postalCode || addr.pinCode}</span>, {addr.country}
                             </p>
                         </div>
                         <div className="flex flex-col items-end gap-2">
                            {/* ACTIONS: EDIT & DELETE */}
                            <div className="flex gap-2">
                                <button onClick={() => triggerEditAddress(addr)} className="text-gray-300 hover:text-teal-600 hover:bg-teal-50 transition-colors p-2 rounded-full" title="Edit Address">
                                    <FontAwesomeIcon icon={faPencilAlt} />
                                </button>
                                <button onClick={() => triggerDeleteAddress(addr.id!)} className="text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors p-2 rounded-full" title="Delete Address">
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                         </div>
                     </div>
                 ))}
                 {addresses.length === 0 && !showAddressForm && (
                     <div className="text-center py-12 flex flex-col items-center justify-center text-gray-400 gap-3 border-2 border-dashed border-gray-100 rounded-lg">
                         <FontAwesomeIcon icon={faMapMarkerAlt} className="text-4xl text-gray-200" />
                         <p className="italic">No addresses saved yet.</p>
                     </div>
                 )}
             </div>
          </div>
        );

      case 'rfqs':
          return (
              <div className="bg-white rounded-md shadow-sm p-6 min-h-[500px]">
                  <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-800">My RFQs (Requests for Quotation)</h2>
                      {!showRFQForm && <button onClick={() => setShowRFQForm(true)} className={`${THEME_BG_TEAL} text-white px-4 py-2 rounded shadow hover:bg-teal-700 font-bold text-sm flex items-center gap-2`}><FontAwesomeIcon icon={faPaperPlane} /> Post New Requirement</button>}
                  </div>
                  {showRFQForm ? (
                      <div className="bg-teal-50 p-6 rounded-lg border border-teal-100 mb-6">
                          <form onSubmit={handleRFQSubmit}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div className="md:col-span-2"><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Product</label><input type="text" className="w-full border p-2 rounded" required value={newRFQ.productName} onChange={e => setNewRFQ({...newRFQ, productName: e.target.value})} /></div>
                                  <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quantity</label><input type="number" className="w-full border p-2 rounded" required value={newRFQ.quantity} onChange={e => setNewRFQ({...newRFQ, quantity: e.target.value})} /></div>
                                  <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Unit</label><select className="w-full border p-2 rounded" value={newRFQ.unit} onChange={e => setNewRFQ({...newRFQ, unit: e.target.value})}><option>Pieces</option><option>Kg</option></select></div>
                                  <div className="md:col-span-2"><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Details</label><textarea className="w-full border p-2 rounded h-24" value={newRFQ.description} onChange={e => setNewRFQ({...newRFQ, description: e.target.value})}></textarea></div>
                              </div>
                              <button type="submit" className="bg-teal-600 text-white px-6 py-2 rounded font-bold mr-2">Submit</button>
                              <button type="button" onClick={() => setShowRFQForm(false)} className="text-gray-500 font-bold px-4">Cancel</button>
                          </form>
                      </div>
                  ) : (
                      <div className="space-y-4">{rfqs.map(rfq => <div key={rfq.id} className="border p-4 rounded bg-white"><h3 className="font-bold">{rfq.productName}</h3><p className="text-sm text-gray-500">{rfq.quantity} • {rfq.date}</p></div>)}</div>
                  )}
              </div>
          );
      default: return <div>Coming Soon</div>;
    }
  };

  if (!user || user.userType !== 'buyer') return <div className="text-center p-20 text-gray-400">Session expired. Please login again.</div>;

  return (
    <div className="bg-gray-100 min-h-screen py-8 font-sans">
      
      {/* --- CUSTOM CONFIRMATION MODAL --- */}
      {confirmModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 animate-fade-in">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 transform transition-all scale-100">
                  <div className="flex items-center gap-3 text-amber-500 mb-4">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl" />
                      <h3 className="text-xl font-bold text-gray-800">{confirmModal.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{confirmModal.message}</p>
                  <div className="flex justify-end gap-3">
                      <button 
                        onClick={() => setConfirmModal(prev => ({...prev, isOpen: false}))}
                        className="px-5 py-2 rounded border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={confirmModal.onConfirm}
                        className={`${THEME_BG_TEAL} px-5 py-2 rounded text-white font-bold hover:bg-teal-700 transition-colors shadow-sm`}
                      >
                        Confirm
                      </button>
                  </div>
              </div>
          </div>
      )}

      <div className="max-w-8xl mx-auto px-4 flex flex-col md:flex-row gap-4 items-start">
         <Sidebar />
         <div className="flex-1 w-full">
            {message && (
                <div className={`p-4 mb-4 rounded-md text-sm font-bold shadow-sm flex items-center gap-2 ${message.type==='success'?'bg-green-100 text-green-800 border border-green-200':'bg-red-100 text-red-800 border border-red-200'}`}>
                    <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faTimes} />
                    {message.text}
                </div>
            )}
            {renderContent()}
         </div>
      </div>
    </div>
  );
};

export default BuyerProfile;